import { ReactElement, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import Icon from '../components/Icon';
import { TAB_SEQUENCE, TAB_THRESHOLD, TAB_UNITS } from './parameter.page';
import { Notificationresponse } from '../constants/telosair-interfaces';
import { getNotification } from '../services/dashboard-service';
import { useData } from '../context/data.context';
import { ROLE_BY_ID } from '../constants/global-constants';
import usePlan from '../hooks/usePlan';
import { isAdminRole } from '../services/session-storage-service';

/**
 * Main Settings page with links to respective sub routes/pages.
 * @returns - A React element that represents the settings page.
 */
export default function SettingsPage(): ReactElement {
  const { totalDevices, totalGroups, totalGateways, clientUsers, currentUser } = useData();
  const { devicePlans } = usePlan();
  // Plans are per device: summarise how many devices sit on each plan.
  const planCounts = devicePlans.reduce<Record<string, number>>((acc, p) => {
    const name = p.effectivePlan || 'No plan';
    acc[name] = (acc[name] || 0) + 1;
    return acc;
  }, {});
  const needsAttention = devicePlans.filter(
    (p) => p.planStatus === 'expiring-soon' || p.planStatus === 'grace-period'
  ).length;
  const nextRenewal = devicePlans
    .filter((p) => p.renewalDate && p.planStatus !== 'expired')
    .map((p) => p.renewalDate as string)
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0];
  const hasAnyPlan = devicePlans.some((p) => p.subscribedPlan);
  const clientName = sessionStorage.getItem('client_name');
  const [notification, setNotification] = useState<Notificationresponse | null>(null);

  /**
   * Fetch the Notifications.
   */
  const fetchNotification = async (): Promise<void> => {
    try {
      const response = await getNotification();
      if (response.data?.length > 0) {
        setNotification(response.data[0]);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  // eslint-disable-next-line jsdoc/require-jsdoc
  const handleDisabledClick = (event: any): void => {
    event.preventDefault();
  };

  useEffect(() => {
    fetchNotification();
  }, []);

  return (
    <div className="my-4">
      {/* Profile */}
      <div className="rounded bg-white dark:bg-container-500 p-4 pl-8">
        <div className="pt-3 text-gray-300 dark:text-gray-300 text-lg pb-6 border-b border-b-gray-200 dark:border-b-gray-900">
          Profile
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-12 mt-4">
          <div className="col-span-3 text-gray-500 dark:text-gray-500 text-lg flex flex-col justify-center gap-6">
            <div>Name</div>
            <div>Role</div>
          </div>
          <div className="col-span-3 col-start-5 text-gray-500 dark:text-gray-300 text-lg flex flex-col justify-center gap-6 leading-none">
            <div>{currentUser?.firstName}</div>
            <div>{currentUser?.role ? ROLE_BY_ID[currentUser?.role] : '-'}</div>
          </div>
          <div className="col-span-4 text-gray-500 dark:text-primary-600 text-lg font-medium  mt-4 md:mt-0">
            <NavLink
              to={{
                pathname: '/settings/profile'
              }}
            >
              <div className="mb-2">Edit Profile</div>
            </NavLink>
            <NavLink
              to={{
                pathname: '/settings/profile'
              }}
              state={{ change: true }}
            >
              <div>Change your Profile Password</div>
            </NavLink>
          </div>
        </div>
      </div>

      {/* Organisation */}
      <div className="rounded bg-white dark:bg-container-500 p-4 mt-4 pl-8">
        <div className="pt-3 text-gray-300 dark:text-gray-300 text-lg pb-6 border-b border-b-gray-200 dark:border-b-gray-900">
          Organization
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-12 mt-4">
          <div className="col-span-3 text-gray-500 dark:text-gray-500 text-lg flex flex-col justify-center gap-6">
            <div>Organization name</div>
            <div>Organization URL</div>
            <div>Language</div>
          </div>
          <div className="col-span-3 col-start-5 text-gray-500 dark:text-gray-300 text-lg flex flex-col justify-center gap-6">
            <div>{clientName}</div>
            <div>-</div>
            <div>-</div>
          </div>
          <div className="col-span-4 text-gray-500 dark:text-primary-600 text-lg font-medium mt-4 md:mt-0">
            <NavLink
              to={{
                pathname: '/settings/organization'
              }}
            >
              <div>Edit Organization Details</div>
            </NavLink>
          </div>
        </div>
      </div>

      {/* Plan Details */}
      <div className="rounded bg-white dark:bg-container-500 p-4 mt-4 pl-8">
        <div className="pt-3 text-gray-300 dark:text-gray-300 text-lg pb-6 border-b border-b-gray-200 dark:border-b-gray-900">
          Plan Details
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-12 mt-4">
          <div className="col-span-3 text-gray-500 dark:text-gray-500 text-lg flex flex-col justify-center gap-6">
            <div>Device plans</div>
            <div>Needs renewal</div>
            <div>Next renewal date</div>
          </div>
          <div className="col-span-3 col-start-5 text-gray-500 dark:text-gray-300 text-lg flex flex-col justify-center gap-6">
            <div>
              {Object.keys(planCounts).length
                ? Object.entries(planCounts)
                    .map(([name, count]) => `${count} ${name}`)
                    .join(' · ')
                : '-'}
            </div>
            <div>
              {needsAttention > 0 ? (
                <span className="inline-flex items-center gap-1 text-warning-300">
                  <span className="material-icons-outlined text-base leading-none">warning</span>
                  {needsAttention} device{needsAttention !== 1 ? 's' : ''}
                </span>
              ) : (
                <span className="text-gray-500">-</span>
              )}
            </div>
            <div>
              {nextRenewal
                ? new Date(nextRenewal).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })
                : '-'}
            </div>
          </div>
          <div className="col-span-4 text-gray-500 dark:text-primary-600 text-lg font-medium mt-4 md:mt-0 flex flex-col gap-6">
            {isAdminRole() ? (
              <NavLink to="/pricing">
                <div>{hasAnyPlan ? 'Manage Plans' : 'Upgrade Plan'}</div>
              </NavLink>
            ) : (
              <div className="text-gray-500 text-base font-normal">
                Only your Admin can buy or change plans
              </div>
            )}
            <NavLink to="/settings/device-setup?tab=devices">
              <div>View plans by device</div>
            </NavLink>
          </div>
        </div>
      </div>

      {/* Notification */}
      <div className="rounded bg-white dark:bg-container-500 p-4 mt-4 pl-8">
        <div className="pt-3 text-gray-300 dark:text-gray-300 text-lg pb-6 border-b border-b-gray-200 dark:border-b-gray-900">
          Notification{' '}
          <div className="w-fit h-5 px-2 py-0.5 ml-2 bg-success-700 rounded justify-start items-center gap-1 inline-flex text-center text-white text-xs">
            <Icon iconName="lock" size="xs" color="text-white" />
            <div className="font-semibold">Coming Soon</div>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-12 mt-4">
          <div className="col-span-3 text-gray-500 dark:text-gray-500 text-lg flex flex-col justify-center gap-6">
            <div>Notify me about</div>
            <div>Send notifications to</div>
            <div>Notify when someone resolves an raised issue </div>
          </div>

          <div
            className="col-span-3 col-start-5 text-gray-500 dark:text-gray-300 text-lg flex flex-col justify-center gap-6"
            key={notification?.id}
          >
            <div>{notification?.events || '-'}</div>
            <div>{notification?.notificationDestination || '-'}</div>

            <div>
              {/* eslint-disable-next-line no-nested-ternary */}
              {notification?.notifyResolvedIssue
                ? 'Yes'
                : notification?.notifyResolvedIssue === false
                ? 'No'
                : '-'}
            </div>
          </div>

          <div className="col-span-5 lg:col-span-4 text-gray-500 dark:text-primary-600 text-lg font-medium  mt-4 md:mt-0 opacity-50">
            <NavLink
              onClick={handleDisabledClick}
              to={{
                pathname: '/settings/notification'
              }}
              state={{
                notifications: notification
              }}
            >
              <div className="mb-2">Manage notification settings</div>
            </NavLink>
            <NavLink
              onClick={handleDisabledClick}
              to={{
                pathname: '/settings/notification'
              }}
              state={{ faultDetection: true, notifications: notification }}
            >
              <div>Enable Fault Detection & Diagnostics (PRO)</div>
            </NavLink>
          </div>
        </div>
      </div>

      {/* Device Setup */}
      <div className="rounded bg-white dark:bg-container-500 p-4 mt-4 pl-8">
        <div className="pt-3 text-gray-300 dark:text-gray-300 text-lg pb-6 border-b border-b-gray-200 dark:border-b-gray-900">
          Device setup
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-12 mt-4">
          <div className="col-span-7 text-gray-500 dark:text-gray-500 text-lg flex gap-2">
            <div className="w-36 text-center p-3 bg-gray-50 rounded dark:bg-background-500 flex flex-col justify-center items-center">
              <p className="text-5xl font-semibold text-success-500 pb-2">{totalDevices}</p>
              <p className="text-xs text-gray-400 font-medium dark:text-gray-400">Total Devices</p>
            </div>
            <div className="w-36 text-center p-3 bg-gray-50 rounded dark:bg-background-500 flex flex-col justify-center items-center">
              <p className="text-5xl font-semibold text-success-500 pb-2">{totalGroups}</p>
              <p className="text-xs text-gray-400 font-medium dark:text-gray-400">Total Groups</p>
            </div>
            <div className="w-36 text-center p-3 bg-gray-50 rounded dark:bg-background-500 flex flex-col justify-center items-center">
              <p className="text-5xl font-semibold text-success-500 pb-2">{totalGateways}</p>
              <p className="text-xs text-gray-400 font-medium dark:text-gray-400">Gateways</p>
            </div>
          </div>
          <div className="col-span-5 text-gray-500 dark:text-primary-600 text-lg font-medium  mt-4 md:mt-0">
            <NavLink to="/settings/device-setup?tab=devices" state={{ addNew: true }}>
              <div className="mb-2">Add a new device</div>
            </NavLink>
            <NavLink to="/settings/device-setup?tab=devices">
              <div className="mb-2">View devices</div>
            </NavLink>
            <NavLink to="/settings/device-setup?tab=groups">
              <div className="mb-2">View groups</div>
            </NavLink>
            <NavLink to="/settings/device-setup?tab=gateways">
              <div className="mb-2">Manage gateways</div>
            </NavLink>
            <NavLink to="/settings/calibration-data">
              <div className="mb-2">Calibrate devices</div>
            </NavLink>
            {/* <NavLink to="/settings/calibration">
              <div>Calibrate devices</div>
            </NavLink> */}
          </div>
        </div>
      </div>

      {/* User management */}
      <div className="rounded bg-white dark:bg-container-500 p-4 mt-4 pl-8">
        <div className="pt-3 text-gray-300 dark:text-gray-300 text-lg pb-6 border-b border-b-gray-200 dark:border-b-gray-900">
          User management
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-12 mt-4">
          <div className="col-span-7 text-gray-500 dark:text-gray-500 text-lg flex gap-2">
            <NavLink
              to="/settings/user-management?tab=users"
              className="w-36 text-center p-3 bg-gray-50 rounded dark:bg-background-500 flex flex-col justify-center items-center"
            >
              <p className="text-5xl font-semibold text-success-500 pb-2">{clientUsers.length}</p>
              <p className="text-xs text-gray-400 font-medium dark:text-gray-400">Total Users</p>
            </NavLink>
            {/* <div className="w-36 text-center p-3 bg-gray-50 rounded dark:bg-background-500">
              <p className="text-5xl font-semibold text-success-500 pb-2">
                {10}
                <span className="material-icons-outlined text-xl mr-1">keyboard_arrow_up</span>{' '}
              </p>
              <p className="text-xs text-gray-400 font-medium dark:text-gray-400">Total Teams</p>
            </div> */}
          </div>
          <div className="col-span-5 text-gray-500 dark:text-primary-600 text-lg font-medium  mt-4 md:mt-0">
            <NavLink to="/settings/user-management?tab=users">
              <div className="mb-2">Manage Users</div>
            </NavLink>
            <NavLink to="/settings/user-management?tab=roles">
              <div>Change Role for Users</div>
            </NavLink>
          </div>
        </div>
      </div>

      {/* Parameters */}
      <div className="rounded bg-white dark:bg-container-500 p-4 mt-4 pl-8">
        <div className="grid grid-cols-2 lg:grid-cols-12 mt-4">
          <div className="col-span-7 text-gray-500 dark:text-gray-500 text-lg flex flex-col justify-center gap-6">
            <div className="pt-3 text-gray-300 dark:text-gray-300 text-lg pb-6">Parameters</div>
          </div>

          <div className="flex flex-col justify-center col-span-5 text-gray-500 dark:text-primary-600 text-lg font-medium">
            <NavLink to={`/settings/parameter?tab=${TAB_THRESHOLD}`}>
              <div className="mb-2">Manage Thresholds</div>
            </NavLink>
            <NavLink to={`/settings/parameter?tab=${TAB_UNITS}`}>
              <div className="mb-2">Manage Units</div>
            </NavLink>
            <NavLink to={`/settings/parameter?tab=${TAB_SEQUENCE}`}>
              <div>Manage Layout</div>
            </NavLink>
          </div>
        </div>
      </div>

      {/* Integrations */}
      <div className="rounded bg-white dark:bg-container-500 p-4 mt-4 pl-8">
        <div className="grid grid-cols-2 lg:grid-cols-12 mt-4">
          <div className="col-span-7 text-gray-500 dark:text-gray-500 text-lg flex flex-col justify-center gap-6">
            <div className="pt-3 text-gray-300 dark:text-gray-300 text-lg pb-6">
              Integrations{' '}
              <div className="w-fit h-5 px-2 py-0.5 ml-2 bg-success-700 rounded justify-start items-center gap-1 inline-flex text-center text-white text-xs">
                <Icon iconName="lock" size="xs" color="text-white" />
                <div className="font-semibold">Coming Soon</div>
              </div>
            </div>
          </div>

          <div className="flex flex-col justify-center col-span-5 text-gray-500 dark:text-primary-600 text-lg font-medium">
            <NavLink
              to={{
                pathname: '/settings/integrations'
              }}
            >
              <div>Manage Integrations</div>
            </NavLink>
          </div>
        </div>
      </div>

      {/* Sharing & Public Access */}
      <div className="rounded bg-white dark:bg-container-500 p-4 mt-4 pl-8">
        <div className="grid grid-cols-2 lg:grid-cols-12 mt-4">
          <div className="col-span-7 text-gray-500 dark:text-gray-500 text-lg flex flex-col justify-center gap-6">
            <div className="pt-3 text-gray-300 dark:text-gray-300 text-lg pb-6">
              Sharing & Public Access
            </div>
          </div>

          <div className="flex flex-col justify-center col-span-5 text-gray-500 dark:text-primary-600 text-lg font-medium">
            <NavLink to="/settings/generate-public-link">
              <div>Generate Public Link</div>
            </NavLink>
          </div>
        </div>
      </div>
    </div>
  );
}
